import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Badge, { readinessVariant } from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import ReadinessModal from '../components/ui/ReadinessModal';
import HospitalFacilityService from '../services/HospitalFacilityService';
import ReadinessLevelService from '../services/ReadinessLevelService';
import AuthService from '../services/AuthService';

const EMPTY_FORM = {
  name: '', code: '', bedCapacity: '', isActive: true,
  fhirBaseUrl: '', authType: 'none', apiKeyHeader: 'Authorization', apiKeyValueRef: '',
};

// Mirrors the server's isFullAdmin — only these callers may change where a hospital's
// FHIR handoff is sent (HospitalFacilityController strips those fields for anyone else).
function isFullAdmin(user) {
  return user?.role === 'admins' || user?.accessTier === 'Admin' || user?.accessTier === 'High Management';
}

function parseAuthConfig(raw) {
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// The delivering process refuses plain HTTP to anything but a local address (see the
// server's FHIR handler), so catch that here rather than on the first failed send.
function fhirUrlProblem(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const local = ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname);
    if (parsed.protocol === 'https:' || (parsed.protocol === 'http:' && local)) return null;
    return 'The FHIR base URL must use https:// (plain http only for localhost).';
  } catch {
    return 'Enter a full FHIR base URL, e.g. https://his.example.ae/fhir';
  }
}

// Identity, bed capacity and readiness for every tier; the HIS/FHIR integration section
// (base URL and how to authenticate) only for full admins.
export default function Hospitals() {
  const canEditIntegration = isFullAdmin(AuthService.getUser());
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readinessLevelIds, setReadinessLevelIds] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const [readinessRow, setReadinessRow] = useState(null);
  const [readinessBusy, setReadinessBusy] = useState(false);

  const loadHospitals = async () => {
    try {
      const response = await HospitalFacilityService.findAll();
      if (response?.status === 200) setHospitals(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const loadReadinessLevels = async () => {
    try {
      const response = await ReadinessLevelService.findAll();
      if (response?.status === 200) {
        const map = {};
        response.data.forEach((rl) => { map[rl.level] = rl.id; });
        setReadinessLevelIds(map);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => { loadHospitals(); loadReadinessLevels(); }, []);

  const openCreate = () => {
    setEditId(null);
    setFormData(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    const authConfig = parseAuthConfig(row.authConfig);
    setFormData({
      name: row.name, code: row.code || '', bedCapacity: row.bedCapacity ?? '', isActive: row.isActive,
      fhirBaseUrl: row.fhirBaseUrl || '',
      authType: row.authType || 'none',
      apiKeyHeader: authConfig.apiKeyHeader || 'Authorization',
      apiKeyValueRef: authConfig.apiKeyValueRef || '',
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.info('Please enter a hospital name');
      return;
    }
    const { fhirBaseUrl, authType, apiKeyHeader, apiKeyValueRef, ...identity } = formData;
    const payload = { ...identity, bedCapacity: formData.bedCapacity === '' ? null : Number(formData.bedCapacity) };
    if (canEditIntegration) {
      const urlProblem = fhirUrlProblem(fhirBaseUrl.trim());
      if (urlProblem) {
        toast.info(urlProblem);
        return;
      }
      if (authType === 'apiKey' && !apiKeyValueRef.trim()) {
        toast.info('Enter the name of the environment variable that holds the API key');
        return;
      }
      payload.fhirBaseUrl = fhirBaseUrl.trim() || null;
      payload.authType = authType;
      payload.authConfig = authType === 'apiKey'
        ? JSON.stringify({ apiKeyHeader: apiKeyHeader.trim() || 'Authorization', apiKeyValueRef: apiKeyValueRef.trim() })
        : null;
    }
    try {
      if (editId) {
        await HospitalFacilityService.update(editId, payload);
        toast.success('Hospital updated successfully');
      } else {
        await HospitalFacilityService.create(payload);
        toast.success('Hospital added successfully');
      }
      setFormOpen(false);
      loadHospitals();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save hospital');
    }
  };

  const openReadiness = (row) => setReadinessRow(row);
  const closeReadiness = () => setReadinessRow(null);

  const handleSetReadiness = async (level) => {
    const readinessLevelId = readinessLevelIds[level];
    if (!readinessLevelId) return;
    setReadinessBusy(true);
    try {
      await HospitalFacilityService.update(readinessRow.id, { readinessLevelId });
      toast.success(`Readiness set to ${level}`);
      closeReadiness();
      loadHospitals();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update readiness level');
    } finally {
      setReadinessBusy(false);
    }
  };

  const headCells = [
    { id: 'name', label: 'Name' },
    { id: 'code', label: 'Code', render: (row) => row.code || '—' },
    { id: 'bedCapacity', label: 'Bed Capacity', render: (row) => row.bedCapacity ?? '—' },
    {
      id: 'isActive',
      label: 'Status',
      render: (row) => <Badge variant={row.isActive ? 'green' : 'neutral'}>{row.isActive ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      id: 'readinessLevel',
      label: 'Readiness',
      render: (row) => {
        const level = row.readiness_level?.level || 'Normal';
        return <Badge variant={readinessVariant(level)}>{level}</Badge>;
      },
    },
  ];

  const options = [{ title: 'Manage Readiness', handler: (row) => openReadiness(row) }];

  return (
    <Sidebar title="Hospitals">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500 dark:text-neutral-500">Hospitals participating in the CBRNE response network.</p>
          <Button icon={Plus} onClick={openCreate}>New Hospital</Button>
        </div>

        <DataTable
          title="Hospitals"
          rows={hospitals}
          headCells={headCells}
          moreOptions={options}
          onEdit={openEdit}
          loading={loading}
          emptyLabel={loading ? 'Loading hospitals…' : 'No hospitals found.'}
        />
      </div>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? 'Edit Hospital' : 'New Hospital'}
        onConfirm={handleSave}
        confirmLabel="Save"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Name</label>
            <input
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">Code</label>
            <input
              className="form-input"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">Bed Capacity</label>
            <input
              type="number"
              min="0"
              className="form-input"
              value={formData.bedCapacity}
              onChange={(e) => setFormData({ ...formData, bedCapacity: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Active</span>
          </label>

          {canEditIntegration && (
            <div className="space-y-4 border-t border-neutral-200 pt-4 dark:border-neutral-700">
              <div>
                <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">HIS / FHIR integration</p>
                <p className="text-xs text-neutral-500 mt-0.5">Where "Send to HIS" delivers this hospital's patient bundles. Leave the URL empty if the hospital isn't connected.</p>
              </div>
              <div>
                <label className="form-label" htmlFor="hospital-fhir-url">FHIR Base URL</label>
                <input
                  id="hospital-fhir-url"
                  className="form-input"
                  placeholder="https://his.example.ae/fhir"
                  value={formData.fhirBaseUrl}
                  onChange={(e) => setFormData({ ...formData, fhirBaseUrl: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="hospital-auth-type">Authentication</label>
                <select
                  id="hospital-auth-type"
                  className="form-input"
                  value={formData.authType}
                  onChange={(e) => setFormData({ ...formData, authType: e.target.value })}
                >
                  <option value="none">None</option>
                  <option value="apiKey">API key</option>
                </select>
              </div>
              {formData.authType === 'apiKey' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label" htmlFor="hospital-api-key-header">Header</label>
                    <input
                      id="hospital-api-key-header"
                      className="form-input"
                      value={formData.apiKeyHeader}
                      onChange={(e) => setFormData({ ...formData, apiKeyHeader: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="hospital-api-key-ref">Key environment variable</label>
                    <input
                      id="hospital-api-key-ref"
                      className="form-input"
                      placeholder="HOSPITAL_X_API_KEY"
                      value={formData.apiKeyValueRef}
                      onChange={(e) => setFormData({ ...formData, apiKeyValueRef: e.target.value })}
                    />
                  </div>
                  <p className="sm:col-span-2 text-xs text-neutral-500 -mt-2">
                    The key itself is never stored here — set it under this variable name on the server that delivers to hospitals.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <ReadinessModal
        open={Boolean(readinessRow)}
        subjectLabel={readinessRow?.name}
        currentLevel={readinessRow?.readiness_level?.level}
        onClose={closeReadiness}
        onSelect={handleSetReadiness}
        busy={readinessBusy}
      />
    </Sidebar>
  );
}
